import React, { useMemo } from "react";
import { Input, Spin } from "../../../BP2Components";
import { AutoGrowTextArea } from "./FormV3TextAreaField";
import { useFieldController } from "../../../../util/useFieldController";
import { useOtherOption } from "../../../../util/useOtherOption";
import { isFieldDisabled } from "./FormFIeldsV3Decorators";
import IntlMessages from "../../../../util/IntlMessages";

import "../formsV3.less";

type FormField = {
   id: string;
   type?: string;
   decorators?: Decorator[];
   [key: string]: unknown;
};

type Decorator = {
   name?: string;
   properties?: {
      options?: string[];
      other?: boolean;
   };
};

type Props = {
   field: FormField;
   formGUID: string;
   loading?: boolean;
   isQuestionnaire?: boolean;
   onValueChange?: (payload: { field: FormField; value: unknown }) => void;
};

const FormV3RadioField: React.FC<Props> = ({
   field,
   formGUID,
   loading,
   isQuestionnaire,
   onValueChange,
}): React.ReactElement => {
   const { value, setValue, fieldInfo } = useFieldController({
      field,
      formGUID,
      onValueChange,
   });

   const optionsDecorator = useMemo(() => {
      return (field?.decorators ?? []).find((decorator) => decorator?.name === "OptionsDecorator");
   }, [field?.decorators]);

   const options: string[] = useMemo(() => {
      const rawOptions = optionsDecorator?.properties?.options;
      return Array.isArray(rawOptions) ? rawOptions : [];
   }, [optionsDecorator]);

   const otherEnabled = optionsDecorator?.properties?.other === true;

   const hasOptions = options.length > 0;

   const selectedValue = useMemo((): string => {
      if (value == null) return "";
      if (typeof value === "string" || typeof value === "number") return String(value);
      if (typeof value === "object" && value !== null && "value" in value) {
         return String((value as Record<string, unknown>).value ?? "");
      }
      return "";
   }, [value]);

   const { isOtherSelected, otherText, toggleOther, handleOtherTextChange } = useOtherOption({
      otherEnabled,
      hasOptions,
      options,
      selectedValues: [selectedValue],
      setValue,
      mode: "single",
   });

   const handleSelect = (nextValue: string) => {
      toggleOther(false);
      setValue(nextValue);
   };

   const OtherField = (isQuestionnaire ? AutoGrowTextArea : Input) as React.ComponentType<{
      disabled: boolean;
      value: string;
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
      style: React.CSSProperties;
   }>;

   return (
      <>
         <div
            className="v3-field-element"
            style={fieldInfo ? { borderColor: "var(--form-warnings)" } : {}}
         >
            {hasOptions ? (
               <div className="v3-options-group">
                  {options.map((optionLabel) => {
                     const inputId = `${field.id}-${optionLabel}`;
                     return (
                        <label
                           key={inputId}
                           htmlFor={inputId}
                           className={`v3-option-label v3-field id-${field.id}`}
                        >
                           <input
                              id={inputId}
                              type="radio"
                              name={`radio-${field.id}`}
                              disabled={isFieldDisabled(field)}
                              checked={selectedValue === optionLabel}
                              onChange={() => handleSelect(optionLabel)}
                           />
                           <span>{optionLabel}</span>
                        </label>
                     );
                  })}

                  {otherEnabled ? (
                     <div className="v3-other-container">
                        <label
                           htmlFor={`${field.id}-other`}
                           className={`v3-option-label v3-field id-${field.id}`}
                        >
                           <input
                              id={`${field.id}-other`}
                              type="radio"
                              name={`radio-${field.id}`}
                              disabled={isFieldDisabled(field)}
                              checked={isOtherSelected}
                              onChange={() => toggleOther(true)}
                           />
                           <span>
                              <IntlMessages id="formField.other" />
                           </span>
                        </label>

                        {isOtherSelected && (
                           <OtherField
                              disabled={isFieldDisabled(field)}
                              value={otherText}
                              onChange={(e) => handleOtherTextChange(e.target.value)}
                              style={{ width: "100%" }}
                           />
                        )}
                     </div>
                  ) : null}
               </div>
            ) : (
               <span className="v3-field-no-options">
                  <IntlMessages id="formField.noOptions" />
               </span>
            )}

            <span className="p2 dev-info">{field.id}</span>
            {fieldInfo ? <div className="validation-error">{fieldInfo}</div> : null}
         </div>

         {loading && <Spin />}
      </>
   );
};

export default FormV3RadioField;
